import React, { useState } from "react";
import PropTypes from "prop-types";

import { Button } from "components/core/Button";
import { StarRating } from "components/review/StarRating";
import Select from "components/core/Select/Select";
import ReviewCard from "components/review/ReviewsList/ReviewCard";
import GoogleReviewCard from "components/molecules/GoogleReviewCard/GoogleReviewCard";
import ReviewTabs, { VERIFIED_TAB, GOOGLE_TAB } from "components/molecules/ReviewTabs/ReviewTabs";
import "./ReviewListV2.scss";

// SORTING ALGOS
export const sortByHighestRated = (reviews) =>
	[...reviews].sort((a, b) => b.rating - a.rating);
export const sortByLowestRated = (reviews) =>
	[...reviews].sort((a, b) => a.rating - b.rating);
export const sortByNewest = (reviews) =>
	[...reviews].sort((a, b) => b.dateNum - a.dateNum);
export const sortByOldest = (reviews) =>
	[...reviews].sort((a, b) => a.dateNum - b.dateNum);

// OPTIONS MAP
const sortOptions = {
	highestRated: {
		label: "Highest Rated",
		value: "highestRated",
		sortingAlgorithm: sortByHighestRated
	},
	lowestRated: {
		label: "Lowest Rated",
		value: "lowestRated",
		sortingAlgorithm: sortByLowestRated
	},
	newest: {
		label: "Newest",
		value: "newest",
		sortingAlgorithm: sortByNewest
	},
	oldest: { label: "Oldest", value: "oldest", sortingAlgorithm: sortByOldest }
};

const ReviewListV2 = ({
	totalReviews,
	averageRating,
	reviews,
	defaultSort,
	reviewsPerPage,
	hideHeading,
	isGoogleReviewEnabled,
	googleReviews
}) => {
	const googleReviewsArray = googleReviews?.reviews || [];
	const googleCount = googleReviews?.user_ratings_total || 0;

	const hasGoogleReviews = isGoogleReviewEnabled && googleCount > 0;
	const hasVerifiedReviews = totalReviews > 0;

	// Determine initial active tab (server-side safe)
	let initialActiveTab = VERIFIED_TAB;
	if (!hasVerifiedReviews && hasGoogleReviews) {
		initialActiveTab = GOOGLE_TAB;
	}

	const [sortType, setSortType] = useState(defaultSort);
	const [verifiedPageCount, setVerifiedPageCount] = useState(0);
	const [activeTab, setActiveTab] = useState(initialActiveTab);

	const MAX_VERIFIED_PAGES = Math.ceil(totalReviews / reviewsPerPage);
	const canLoadMoreVerified = verifiedPageCount < MAX_VERIFIED_PAGES - 1;

	const handleChange = (e) => {
		setSortType(e.target.value);
	};

	const showTabs = hasGoogleReviews && hasVerifiedReviews;

	const displayRating = activeTab === GOOGLE_TAB && hasGoogleReviews
		? googleReviews?.rating || 0
		: averageRating || 0;

	const displayCount = activeTab === GOOGLE_TAB && hasGoogleReviews
		? googleCount || 0
		: totalReviews || 0;

	// Sort all reviews for SSR (all content in page source)
	const sortedVerifiedReviews = sortOptions[sortType].sortingAlgorithm(reviews);
	const visibleVerifiedReviews = sortedVerifiedReviews.slice(0, reviewsPerPage * (verifiedPageCount + 1));

	return (
		<div className="facility-reviews">
			<div className="facility-reviews--subheader">
				<div className="facility-reviews--rating">
					<StarRating
						outOf={5}
						rating={displayRating}
					/>
					<span className="average-rating-number">
						{(displayRating || 0).toFixed(1)}
					</span>
					<span className="total-reviews-text">
						{`(${displayCount})`}
					</span>
				</div>

				{(showTabs || (hasVerifiedReviews && (!showTabs || activeTab === VERIFIED_TAB))) && (
					<div className="facility-reviews--controls">
						{(!showTabs || activeTab === VERIFIED_TAB) && hasVerifiedReviews && (
							<Select
								className="reviews-sort"
								id="sort-select"
								label="Sort By:"
								options={Object.values(sortOptions)}
								onChange={handleChange}
								defaultValue={defaultSort}
								labelProps={{ color: "darkGray" }}
							/>
						)}

						{showTabs && (
							<div className="facility-reviews--tabs-wrapper">
								<ReviewTabs
									verifiedCount={totalReviews}
									googleCount={googleCount}
									activeTab={activeTab}
									onTabChange={setActiveTab}
								/>
							</div>
						)}
					</div>
				)}
			</div>

			<div className={`review-cards-container ${activeTab === VERIFIED_TAB ? 'active' : ''}`} data-tab="verified">
				{hasVerifiedReviews && (
					<>
						<div className="review-cards">
							{visibleVerifiedReviews.map((review) => (
								<ReviewCard
									key={review.id}
									{...review}
									hideHeading={hideHeading}
									hideRatingText
								/>
							))}
						</div>

						{canLoadMoreVerified && (
							<Button
								theme="primary"
								onClick={() => setVerifiedPageCount(verifiedPageCount + 1)}
								className="load-more-reviews-button"
							>
								Load More Reviews
							</Button>
						)}
					</>
				)}
			</div>

			{/* Render ALL Google reviews in page source for SEO */}
			<div className={`review-cards-container ${activeTab === GOOGLE_TAB ? 'active' : ''}`} data-tab="google">
				{hasGoogleReviews && (
					<>
						<div className="review-cards">
							{googleReviewsArray.map((review) => (
								<GoogleReviewCard
									key={`${review.author_name}-${review.time}`}
									reviewerName={review.author_name}
									reviewerPhoto={review.profile_photo_url}
									rating={review.rating}
									reviewDate={review.time}
									reviewText={review.text}
								/>
							))}
						</div>
						<a
							href={`https://www.google.com/maps/search/?api=1&query=Google&query_place_id=${encodeURIComponent(googleReviews?.placeId || '')}`}
							target="_blank"
							rel="noopener noreferrer"
							style={{ textDecoration: 'none' }}
						>
							<Button
								theme="primary"
								className="load-more-reviews-button"
							>
								Read All Reviews on Google
							</Button>
						</a>
					</>
				)}
			</div>
		</div>
	);
};

ReviewListV2.defaultProps = {
	reviews: [],
	totalReviews: 0,
	averageRating: 0,
	defaultSort: "highestRated",
	reviewsPerPage: 5,
	hideHeading: false,
	isGoogleReviewEnabled: false,
	googleReviews: null
};

ReviewListV2.propTypes = {
	reviews: PropTypes.arrayOf(PropTypes.shape(ReviewCard.propTypes)),
	totalReviews: PropTypes.number,
	averageRating: PropTypes.number,
	defaultSort: PropTypes.string,
	reviewsPerPage: PropTypes.number,
	hideHeading: PropTypes.bool,
	isGoogleReviewEnabled: PropTypes.bool,
	googleReviews: PropTypes.shape({
		rating: PropTypes.number,
		user_ratings_total: PropTypes.number,
		placeId: PropTypes.string,
		reviews: PropTypes.arrayOf(PropTypes.object)
	})
};

export default ReviewListV2;
